import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { Shimmer } from "./StatCard";

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ icon, title = "Nothing here yet", description, action }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: `${t.s[9]}px ${t.s[5]}px`, gap: t.s[3] }}>
      <span style={{
        width: 48, height: 48, borderRadius: t.r.md, display: "inline-flex", alignItems: "center",
        justifyContent: "center", background: t.c.surfaceMuted, color: t.c.textTertiary,
      }}>{icon || <Inbox size={22} />}</span>
      <div style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.semibold, color: t.c.text }}>{title}</div>
      {description && <div style={{ fontSize: t.font.size.md, color: t.c.textTertiary, maxWidth: 320 }}>{description}</div>}
      {action && <div style={{ marginTop: t.s[2] }}>{action}</div>}
    </div>
  );
}

// ── DataTable ────────────────────────────────────────────────────────────────
// columns: [{ key, header, align, width, sortable, render(row) }]
// Clean, dense, hover-highlighted rows; animated row reveal; sticky header.
export function DataTable({
  columns, rows, rowKey = (r, i) => r.id ?? r._id ?? i,
  sortKey, sortDir, onSort, loading, skeletonRows = 5, onRowClick, emptyState,
}) {
  const t = useDashTheme();

  const headerCell = (col) => {
    const active = sortKey === col.key;
    return (
      <th
        key={col.key}
        onClick={col.sortable ? () => onSort?.(col.key) : undefined}
        style={{
          textAlign: col.align || "left", padding: `0 ${t.s[4]}px`, height: 36,
          fontSize: t.font.size.sm, fontWeight: t.font.weight.regular,
          color: active ? t.c.text : t.c.textSecondary,
          whiteSpace: "nowrap", userSelect: "none",
          cursor: col.sortable ? "pointer" : "default", width: col.width,
          position: "sticky", top: 0, background: t.c.surface, zIndex: 1,
          borderBottom: `1px solid ${t.c.border}`,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: col.align === "right" ? "flex-end" : "flex-start", width: col.align === "right" ? "100%" : "auto" }}>
          {col.header}
          {col.sortable && active && (sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
        </span>
      </th>
    );
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: t.font.family }}>
        <thead><tr>{columns.map(headerCell)}</tr></thead>
        <tbody>
          {loading ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <tr key={`sk-${i}`} style={{ borderBottom: `1px solid ${t.c.border}` }}>
                {columns.map((col, ci) => (
                  <td key={col.key} style={{ padding: `${t.s[3]}px ${t.s[4]}px`, height: 44 }}>
                    <Shimmer w={ci === 0 ? "70%" : "50%"} h={12} />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} style={{ padding: 0 }}>{emptyState || <EmptyState />}</td></tr>
          ) : (
            <AnimatePresence initial={false}>
              {rows.map((row, i) => (
                <motion.tr
                  key={rowKey(row, i)}
                  className="tg-datarow"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.2) }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{ borderBottom: `1px solid ${t.c.border}`, cursor: onRowClick ? "pointer" : "default", transition: "background 120ms ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = t.c.surfaceMuted; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  {columns.map((col) => (
                    <td key={col.key} style={{
                      textAlign: col.align || "left", padding: `${t.s[3]}px ${t.s[4]}px`,
                      fontSize: t.font.size.base, color: t.c.text, height: 44, verticalAlign: "middle",
                      width: col.width,
                    }}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </AnimatePresence>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
export function Pagination({
  page, pages, total, onChange, pageLabel = "items",
  pageSize, onPageSizeChange, pageSizeOptions = [10, 20, 50],
  shown,
}) {
  const t = useDashTheme();
  // Keep the bar when a per-page selector is present: on a single page it's the
  // only way back to a smaller page size.
  if ((!pages || pages <= 1) && !onPageSizeChange) return null;
  const btn = (disabled, onClick, children) => (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
        borderRadius: t.r.sm, border: `1px solid ${t.c.border}`, background: t.c.surface,
        color: disabled ? t.c.textTertiary : t.c.text, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >{children}</button>
  );
  // Which slice of `total` is on screen — "1–8 of 510" reads far better than a bare
  // count, and makes it obvious the list is paginated rather than truncated.
  //
  // Prefer the real page size when the caller knows it: ceil(total / pages) is a
  // guess that goes wrong on the last page (13 items over 2 pages of 12 reads as
  // "1–7 of 13"). `shown` covers callers that drop rows after fetching, so the
  // range never claims more rows than the table actually renders.
  const perPage = pageSize || (total != null && pages > 0 ? Math.ceil(total / pages) : null);
  const from = perPage ? (page - 1) * perPage + 1 : null;
  const to = perPage
    ? (shown != null ? from + Math.max(shown, 1) - 1 : Math.min(page * perPage, total))
    : null;

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[3], flexWrap: "wrap", padding: `${t.s[3]}px ${t.s[5]}px`, borderTop: `1px solid ${t.c.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: t.s[3], flexWrap: "wrap" }}>
        <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
          {total != null
            ? (perPage ? `${from}–${to} of ${total} ${pageLabel}` : `${total} ${pageLabel}`)
            : `Page ${page} of ${pages}`}
        </span>
        {onPageSizeChange && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>Per page</span>
            {pageSizeOptions.map((n) => (
              <button
                key={n}
                onClick={() => onPageSizeChange(n)}
                style={{
                  height: 24, minWidth: 28, padding: "0 6px", borderRadius: t.r.xs, cursor: "pointer",
                  border: "none", fontFamily: t.font.family, fontSize: t.font.size.sm,
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: n === pageSize ? 600 : 400,
                  background: n === pageSize ? t.c.surfaceMuted : "transparent",
                  color: n === pageSize ? t.c.text : t.c.textTertiary,
                }}
              >{n}</button>
            ))}
          </span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: t.s[2] }}>
        {/* Jump to first/last — with 60+ pages, stepping one at a time is unusable. */}
        {pages > 2 && btn(page <= 1, () => onChange(1), <ChevronsLeft size={16} />)}
        {btn(page <= 1, () => onChange(page - 1), <ChevronLeft size={16} />)}
        <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, minWidth: 70, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
          {page} / {pages}
        </span>
        {btn(page >= pages, () => onChange(page + 1), <ChevronRight size={16} />)}
        {pages > 2 && btn(page >= pages, () => onChange(pages), <ChevronsRight size={16} />)}
      </div>
    </div>
  );
}
